// CreatePromptScreen.js
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { CustomTextArea, TextArea } from "../../../components";
import { AI_MARKETING, templateEndpoints } from "../../../utils/constants/httpConstants";
import { getRequest, POST, postRequest } from "../../../Apis/apiWrapper";
import { STRINGS } from "../strings";
import { Button } from "../../../components/Buttons";
import TypographyInline from "../../../components/Typography/TypographyInline";
import { BRAND, GREY } from "../../../utils/constants/colors";
import { fontSize, fontWeight } from "../../../utils/constants/UI";
import CustomHeader from "../../../components/Header/CustomHeader";
import { AI_EMAIL_MARKETING_PROMPT_GENERATE_PATH } from "../../../utils/constants/routes";
import InputField from "../../../components/InputFields/InputField";
import { AutoCompleteCustom } from "../../../components/Autocomplete/AutoCompleteCustom";
import RadioButton from "../../../components/RadioButton/RadioButton";
import classes from "./../../Marketing/components/markComponents.module.css";
import Typography from "../../../components/Typography/Typography";
import { generateOpenAIResponse } from "../../../utils/helpers/OpenAPIgen";
import EventEmitter from "../../../utils/emitter/EventEmitter";
import { EMITTER_ERROR, EMITTER_SUCCESS } from "../../../utils/constants";
import { path } from "d3";

const CreatePrompt = () => {
  const location = useLocation();
  const { tempId, prId } = useParams();
  const navigate = useNavigate();

  const userDet = useSelector((state) => state.userDetails);
  const owner = userDet?.payload;

  // Determine the current mode based on location state
  const isEditing = location?.state?.isEditing || false;
  const isViewing = location?.state?.isViewing || false;
  const promptId = location?.state?.promptId || null;

  // Determine the current mode
  const currentMode = isViewing ? "view" : isEditing ? "edit" : "create";

  const [tempName, setTempName] = useState("");
  const [promptBody, setPromptBody] = useState("");
  const [viewType, setViewType] = useState(
    location?.state?.permit || STRINGS.PRIVATE_ID
  );
  const MODEL_OPTIONS = {
    openai: [
      // ----- GPT-5.x -----
      // { id: "gpt-5", label: "GPT-5 (Flagship)" },
      // { id: "gpt-5.1", label: "GPT-5.1 (Flagship)" },
      // { id: "gpt-5.1-chat-latest", label: "GPT-5.1 Chat (Latest)" },
      // { id: "gpt-5-chat-latest", label: "GPT-5 Chat (Latest)" },
      // { id: "gpt-5-2025-08-07", label: "GPT-5 (2025-08-07)" },
      // { id: "gpt-5-mini", label: "GPT-5 Mini" },
      // { id: "gpt-5-mini-2025-08-07", label: "GPT-5 Mini (2025-08-07)" },
      // { id: "gpt-5-nano", label: "GPT-5 Nano" },
      // { id: "gpt-5-nano-2025-08-07", label: "GPT-5 Nano (2025-08-07)" },
      // ----- GPT-4.1 -----
      { id: "gpt-4.1", label: "GPT-4.1" },
      { id: "gpt-4.1-2025-04-14", label: "GPT-4.1 (2025-04-14)" },
      { id: "gpt-4.1-mini", label: "GPT-4.1 Mini" },
      { id: "gpt-4.1-mini-2025-04-14", label: "GPT-4.1 Mini (2025-04-14)" },
      { id: "gpt-4.1-nano", label: "GPT-4.1 Nano" },
      { id: "gpt-4.1-nano-2025-04-14", label: "GPT-4.1 Nano (2025-04-14)" },
      // ----- GPT-4o -----
      { id: "gpt-4o", label: "GPT-4o (Omni)" },
      { id: "gpt-4o-2024-05-13", label: "GPT-4o (2024-05-13)" },
      { id: "gpt-4o-2024-08-06", label: "GPT-4o (2024-08-06)" },
      { id: "gpt-4o-2024-11-20", label: "GPT-4o (2024-11-20)" },
      { id: "gpt-4o-mini", label: "GPT-4o Mini" },
      { id: "gpt-4o-mini-2024-07-18", label: "GPT-4o Mini (2024-07-18)" },
      // ----- GPT-4 Turbo -----
      { id: "gpt-4-turbo", label: "GPT-4 Turbo" },
      { id: "gpt-4-turbo-2024-04-09", label: "GPT-4 Turbo (2024-04-09)" },
      { id: "gpt-4-turbo-preview", label: "GPT-4 Turbo (Preview)" },
      // ----- GPT-4 -----
      { id: "gpt-4-0125-preview", label: "GPT-4 (0125 Preview)" },
      { id: "gpt-4", label: "GPT-4" },
      { id: "gpt-4-0613", label: "GPT-4 (0613)" },
      // ----- GPT-3.5 -----
      { id: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
      { id: "gpt-3.5-turbo-0125", label: "GPT-3.5 Turbo (0125)" },
      { id: "gpt-3.5-turbo-1106", label: "GPT-3.5 Turbo (1106)" },
      { id: "gpt-3.5-turbo-16k", label: "GPT-3.5 Turbo (16k)" },
    ],
    anthropic: [
      // :fire: Latest 2025 Models
      { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
      { id: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5" },
      { id: "claude-opus-4-1-20250805", label: "Claude Opus 4.1" },
      // :fire: Mid-2025 Releases
      { id: "claude-opus-4-20250514", label: "Claude Opus 4" },
      { id: "claude-sonnet-4-20250514", label: "Claude Sonnet 4" },
      // 2025 Early
      { id: "claude-3-7-sonnet-20250219", label: "Claude Sonnet 3.7" },
      // 2024 Models
      { id: "claude-3-5-haiku-20241022", label: "Claude Haiku 3.5" },
      { id: "claude-3-5-sonnet-20241022", label: "Claude Sonnet 3.5 (New)" },
      // Older 3.5 Model
      { id: "claude-3-5-sonnet-20240620", label: "Claude Sonnet 3.5 (Old)" },
      // 2024 Earlier Releases
      { id: "claude-3-haiku-20240307", label: "Claude Haiku 3" },
      { id: "claude-3-opus-20240229", label: "Claude Opus 3" },
    ],
  };
  const SERVICE_OPTIONS = [
    { id: "openai", label: "Open AI" },
    { id: "anthropic", label: "Anthropic" },
  ];

  const [aiModelSearch, setAiModelSearch] = useState("");
  const [filteredAiModelList, setFilteredAiModelList] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [serviceValueState, setServiceValueState] = useState([]);  // label
  const [serviceIdState, setServiceIdState] = useState([]);        // id
  const [selectedService, setSelectedService] = useState("");      // final value used internally
  const [modelValueState, setModelValueState] = useState([]);
  const [modelIdState, setModelIdState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [valueState, setValueState] = useState([]);
  const [idVal, setIdVal] = useState([]);
  const [validations, setValidations] = useState({ name: "", promptBody: "" });
  const [dataList, setDataList] = useState([]);
  const [isFollowUp, setIsFollowUp] = useState(false); // default: New
  const [generatedEmail, setGeneratedEmail] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  

  // Dynamic header items based on current mode
  // const [headerItems, setHeaderItems] = useState([
  //   { id: "prompts", label: STRINGS.PROMPT_BREADCRUMBS },
  //   {
  //     id: "current",
  //     label:
  //       currentMode === "view"
  //         ? "View Prompt"
  //         : currentMode === "edit"
  //         ? "Edit Prompt"
  //         : STRINGS.PROMPT_HEADER,
  //   },
  //   { path: AI_EMAIL_MARKETING_PROMPT_GENERATE_PATH },
  // ]);

  const [headerItems, setHeaderItems] = useState([
    {
      id: "prompts",
      label: STRINGS.PROMPT_BREADCRUMBS,
      path: AI_EMAIL_MARKETING_PROMPT_GENERATE_PATH,
    },
    {
      id: "current",
      label:
        currentMode === "view"
          ? "View Prompt"
          : currentMode === "edit"
            ? "Edit Prompt"
            : STRINGS.PROMPT_HEADER,
      path: null, // current page, so no navigation
    },
  ]);

  const [radioButtonData] = useState([
    { id: STRINGS.PRIVATE_ID, label: STRINGS.PRIVATE_LABEL },
    { id: STRINGS.TEAM_ID, label: STRINGS.TEAM_LABEL },
  ]);

  const [promptTypeRadioData] = useState([
    { id: "new", label: "New" },
    { id: "followUp", label: "Follow Up" },
  ]);

  // Function to handle switching from view to edit mode
  const handleSwitchToEdit = () => {
    navigate(location.pathname, {
      state: {
        isEditing: true,
        promptId: promptId,
      },
      replace: true,
    });
  };

  const handleChange = (event) => {
    if (currentMode !== "view") {
      setViewType(event.target.value);
    }
  };

  // near render or above JSX (wherever convenient)
  const modelItems = (MODEL_OPTIONS[selectedService] || []).map(m => ({
    id: m.id,
    label: m.label,
    // optionally add iconSrc or iconLabel if you have icons: iconSrc: m.iconSrc
  }));

  const handleModelACChange = ({ id = [], label = [] }) => {
    if (id && id.length) {
      setSelectedModel(id[0]);      // main string used elsewhere
    } else {
      setSelectedModel('');
    }
    setModelIdState(id || []);
    setModelValueState(label || []);
  };

  const handleServiceACChange = ({ id = [], label = [] }) => {
    if (id && id.length) {
      setSelectedService(id[0]); // main string for backend logic
    } else {
      setSelectedService("");
    }

    setServiceIdState(id || []);
    setServiceValueState(label || []);

    // Clear selected model because service changed
    setSelectedModel("");
    setModelIdState([]);
    setModelValueState([]);
  };

  // const handlePromptTypeChange = (event) => {
  //   if (currentMode !== "view") {
  //     setIsFollowUp(event.target.value === "followUp");
  //   }
  // };

  const handleCancel = () => {
    navigate(AI_EMAIL_MARKETING_PROMPT_GENERATE_PATH);
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleGeneratedEmailChange = (value) => {
    if (currentMode !== "view") {
      setGeneratedEmail(value);
    }
  };

  const handleAiModelSearch = (e) => {
    const value = e.target.value;
    setAiModelSearch(value);

    const allModels = MODEL_OPTIONS[selectedService] || [];

    if (value.trim()) {
      const filtered = allModels.filter((item) => 
        item.label.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredAiModelList(filtered);
    } else {
      setFilteredAiModelList(allModels);
    }
  };

  const folderOptions = dataList.map((item) => ({
    label: item?.name,
    id: item?._id,
  }));

  const validateForm = () => {
    let isValid = true;
    const newValidations = { name: "", promptBody: "" };

    if (!tempName.trim()) {
      newValidations.name = "Prompt name is required";
      isValid = false;
    }

    if (!promptBody.trim()) {
      newValidations.promptBody = "Prompt content is required";
      isValid = false;
    }

    setValidations(newValidations);
    return isValid;
  };

  // const generateResult = async (e) => {
  //   if (e && e.preventDefault) e.preventDefault();
  //   if (currentMode === "view") return;

  //   if (!promptBody.trim()) {
  //     setValidations({
  //       ...validations,
  //       promptBody: "Please enter a prompt before generating",
  //     });
  //     return;
  //   }

  //   setIsGenerating(true);

  //   try {
  //     console.log("Selected Model:", selectedModel);
  //     console.log("Prompt Body:", promptBody);

  //     const emailContent = await generateOpenAIResponse({
  //       prompt: promptBody,
  //       model: selectedModel,
  //     });

  //     console.log("Generated Content:", emailContent);
  //     setGeneratedEmail(emailContent);
  //     setValidations({ ...validations, promptBody: "" });
  //   } catch (error) {
  //     console.error("Error generating content:", error);
  //     setGeneratedEmail("Error generating response.");
  //   }
  //   setIsGenerating(false);
  // };

  // const fetchPromptData = async id => {
  //   try {
  //     setLoading(true)
  //     const response = await getRequest(navigate, `${AI_MARKETING.getPromptById}/${id}`)

  //     if (response?.status === 200 && response?.data?.data) {
  //       const promptData = response.data.data

  //       setTempName(promptData.name || '')
  //       setGeneratedEmail(promptData.prompt || '')
  //       setPromptBody(promptData.additionalDetails || '')

  //       if (promptData.viewType) {
  //         setViewType(promptData.viewType)
  //       }

  //       if (promptData.selectedModel) {
  //         setSelectedModel(promptData.selectedModel)
  //       }
  //     } else {
  //       console.error('Error fetching prompt data:', response)
  //       EventEmitter.emit(EMITTER_ERROR, 'Failed to load prompt data')
  //     }
  //   } catch (error) {
  //     console.error('Error fetching prompt data:', error)
  //     EventEmitter.emit(
  //       EMITTER_ERROR,
  //       error?.data?.message || error?.message || 'Failed to load prompt data'
  //     )
  //   } finally {
  //     setLoading(false)
  //   }
  // }

  const generateResult = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (currentMode === "view") return;

    if (!promptBody.trim()) {
      setValidations({
        ...validations,
        promptBody: "Please enter a prompt before generating",
      });
      return;
    }

    setIsGenerating(true);

    try {
      // console.log("Selected Model:", selectedModel);
      // console.log("Prompt Body:", promptBody);

      const emailContent = await generateOpenAIResponse({
        provider: selectedService,
        prompt: promptBody,
        model: selectedModel,
      });

      // NEW: emailContent already equals response.data.data.content
      // console.log("Generated Content:", emailContent);

      setGeneratedEmail(emailContent || "No response generated");
      setValidations({ ...validations, promptBody: "" });
    } catch (error) {
      console.error("Error generating content:", error);
      setGeneratedEmail("Error generating response.");
    }

    setIsGenerating(false);
  };

  const fetchPromptData = async (id) => {
    try {
      setLoading(true);
      const response = await getRequest(
        navigate,
        `${AI_MARKETING.getPromptById}/${id}`
      );

      if (response?.status === 200 && response?.data?.data) {
        const promptData = response.data.data;

        setTempName(promptData.name || "");
        setGeneratedEmail(promptData.prompt || "");
        setPromptBody(promptData.additionalDetails || "");

        if (promptData.viewType) {
          setViewType(promptData.viewType);
        }

        if (promptData.selectedModel) {
          const sm = (promptData.selectedModel || '').toLowerCase();
          // infer service (you already do this). ensure selectedService is set before this runs.
          let inferredSvc = "";
          if (sm.includes("claude")) inferredSvc = "anthropic";
          else if (sm.includes("gpt") || sm.includes("chat") || sm.includes("openai")) inferredSvc = "openai";
          if (inferredSvc) setSelectedService(inferredSvc);

          // find label in MODEL_OPTIONS and set value/id states for AutoCompleteCustom
          const options = MODEL_OPTIONS[inferredSvc] || [];
          const found = options.find(m => m.id === promptData.selectedModel || m.id === promptData.selectedModel.toLowerCase());
          if (found) {
            setModelValueState([found.label]);
            setModelIdState([found.id]);
          } else {
            // fallback: show what backend has (label/id)
            setModelValueState([promptData.selectedModel]);
            setModelIdState([promptData.selectedModel]);
          }
          setSelectedModel(promptData.selectedModel);
        }

        // Add this line to load isFollowUp from saved data
        if (promptData.isFollowUp !== undefined) {
          setIsFollowUp(promptData.isFollowUp);
        }
      } else {
        console.error("Error fetching prompt data:", response);
        EventEmitter.emit(EMITTER_ERROR, "Failed to load prompt data");
      }
    } catch (error) {
      console.error("Error fetching prompt data:", error);
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || error?.message || "Failed to load prompt data"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tempName) {
      setHeaderItems([
        {
          id: "prompts",
          label: STRINGS.PROMPT_BREADCRUMBS,
          path: AI_EMAIL_MARKETING_PROMPT_GENERATE_PATH, // clickable
        },
        {
          id: "current",
          label:
            currentMode === "view"
              ? tempName
              : currentMode === "edit"
                ? `Edit: ${tempName}`
                : STRINGS.PROMPT_HEADER,
          path: null, // not clickable (current breadcrumb)
        },
      ]);
    }
  }, [tempName, currentMode]);

  useEffect(() => {
    if ((isEditing || isViewing) && promptId) {
      fetchPromptData(promptId);
    }
  }, [isEditing, isViewing, promptId]);

  // const submitData = async () => {
  //   if (!validateForm()) return;
  //   setLoading(true);
  //   try {
  //     const payload = {
  //       name: tempName,
  //       prompt: generatedEmail,
  //       targetIndustry:
  //         "redundant field is not getting used in prompt creation",
  //       keyMessage: "redundant field is not getting used in prompt creation",
  //       challengesAddressed:
  //         "redundant field is not getting used in prompt creation",
  //       serviceFocus: [
  //         "redundant field is not getting used in prompt creation",
  //       ],
  //       serviceDescription:
  //         "redundant field is not getting used in prompt creation",
  //       callToAction: "redundant field is not getting used in prompt creation",
  //       toneStyle: "redundant field is not getting used in prompt creation",
  //       additionalDetails: promptBody,
  //       isFollowUp,
  //     };

  //     let response;
  //     if (isEditing && promptId) {
  //       response = await postRequest(
  //         navigate,
  //         `${AI_MARKETING.updatePrompt}/${promptId}`,
  //         payload
  //       );
  //     } else {
  //       response = await postRequest(
  //         navigate,
  //         templateEndpoints.promptCreationApi,
  //         payload
  //       );
  //     }

  //     if (response?.data?.status) {
  //       EventEmitter.emit(
  //         EMITTER_SUCCESS,
  //         isEditing
  //           ? "Prompt updated successfully!"
  //           : "Prompt created successfully!"
  //       );
  //       navigate(AI_EMAIL_MARKETING_PROMPT_GENERATE_PATH);
  //     }
  //   } catch (error) {
  //     EventEmitter.emit(
  //       EMITTER_ERROR,
  //       error?.data?.message || error?.message || "Unknown error"
  //     );
  //     console.error(
  //       "Error during submission:",
  //       error?.data?.message || error?.message || "Unknown error"
  //     );
  //   } finally {
  //     setLoading(false);
  //   }
  // };


const submitData = async () => {
  if (!validateForm()) return;
  setLoading(true);

  try {
    const payload = {
      name: tempName,
      prompt: promptBody,
      // targetIndustry: "redundant field is not getting used in prompt creation",
      // keyMessage: "redundant field is not getting used in prompt creation",
      // challengesAddressed:
      //   "redundant field is not getting used in prompt creation",
      // serviceFocus: [
      //   "redundant field is not getting used in prompt creation",
      // ],
      // serviceDescription:
      //   "redundant field is not getting used in prompt creation",
      // callToAction: "redundant field is not getting used in prompt creation",
      // toneStyle: "redundant field is not getting used in prompt creation",
      // additionalDetails: promptBody,
      // isFollowUp,
    };

    // 🔍 LOG PAYLOAD + URL BEFORE CALL
    console.log("➡️ SUBMITTING PAYLOAD:", payload);
    console.log("➡️ EDIT MODE:", isEditing, " | Prompt ID:", promptId);

    let url = isEditing && promptId 
      ? `${AI_MARKETING.updatePrompt}/${promptId}`
      : templateEndpoints.promptCreationApi;

    console.log("➡️ API URL:", url);

    let response;

    if (isEditing && promptId) {
      response = await postRequest(navigate, url, payload);
    } else {
      response = await postRequest(navigate, url, payload);
    }

    // 🔍 LOG SUCCESS RESPONSE
    console.log("✅ API SUCCESS RESPONSE:", response);

    if (response?.data?.status) {
      EventEmitter.emit(
        EMITTER_SUCCESS,
        isEditing
          ? "Prompt updated successfully!"
          : "Prompt created successfully!"
      );
      navigate(AI_EMAIL_MARKETING_PROMPT_GENERATE_PATH);
    }

  } catch (error) {
    // 🔥 LOG RAW ERROR
    console.error("❌ RAW ERROR:", error);

    // 🔥 LOG AXIOS ERROR DETAILS
    console.error("❌ ERROR RESPONSE:", error?.response);
    console.error("❌ ERROR RESPONSE DATA:", error?.response?.data);
    console.error("❌ ERROR MESSAGE:", error?.message);

    EventEmitter.emit(
      EMITTER_ERROR,
      error?.response?.data?.message ||
        error?.message ||
        "Unknown error"
    );
  } finally {
    setLoading(false);
  }
};


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "auto",
      }}
    >
      <CustomHeader items={headerItems} />

      {/* Main Content Container */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: "20px",
          overflow: "visible",
          margin: "0 auto",
          width: "80%",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div style={{ width: "100%" }}>
            <TypographyInline
              size={"lg"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.SEMIBOLD}
              label={
                currentMode === "view"
                  ? "View Prompt"
                  : currentMode === "edit"
                    ? "Edit Prompt"
                    : STRINGS.PROMPT_TEMPLATE
              }
            />
          </div>
          <div style={{ width: "10%" }}>
            {currentMode === "view" && (
              <Button
                label="Edit Prompt"
                variant="primary"
                size="sm36"
                onClick={handleSwitchToEdit}
              />
            )}
          </div>
        </Box>

        {/* Compact Form Section */}
        <Box sx={{ marginBottom: "20px" }}>
          {/* First Row: Prompt Name, Owner, Folder */}
          <Box
            sx={{
              display: "flex",
              gap: "16px",
              marginBottom: "16px",
              alignItems: "flex-end",
            }}
          >
            {/* Prompt Name */}
            <Box sx={{ flex: 1 }}>
              <TypographyInline
                size={"sm"}
                color={GREY.QUATINARY}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.PROMPT_NAME}
                style={{ marginBottom: "8px", display: "block" }}
              />
              <InputField
                autoComplete="off"
                value={tempName}
                placeholder={STRINGS.TYPETEMPLATE}
                variant={
                  validations.name
                    ? "error"
                    : currentMode === "view"
                      ? "disabled"
                      : "default"
                }
                errormessage={validations.name}
                size={"sm36"}
                disabled={prId || currentMode === "view"}
                onChange={(e) =>
                  currentMode !== "view" && setTempName(e.target.value)
                }
              />
            </Box>

            {/* Owner */}
            <Box sx={{ flex: 1 }}>
              <TypographyInline
                size={"sm"}
                color={GREY.QUATINARY}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.OWNER_BY}
                style={{ marginBottom: "8px", display: "block" }}
              />
              <InputField
                autoComplete="off"
                variant={"disabled"}
                value={`${owner?.first_name || ""} ${owner?.last_name || ""}`}
                size={"sm36"}
                disabled
              />
            </Box>

            {/* Folder */}
            <Box sx={{ flex: 1 }}>
              <TypographyInline
                size={"sm"}
                color={GREY.QUATINARY}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.PROMPT_FOLDER}
                style={{ marginBottom: "8px", display: "block" }}
              />
              <AutoCompleteCustom
                items={folderOptions}
                LeftIcon=""
                valueState={valueState}
                setValueState={
                  currentMode !== "view" ? setValueState : () => { }
                }
                idState={idVal}
                setIdState={currentMode !== "view" ? setIdVal : () => { }}
                variant={
                  prId || currentMode === "view" ? "disabled" : "default"
                }
                papermenuclass={classes.paperMenu1}
                size={"sm36"}
              />
            </Box>
          </Box>

          {/* Second Row: AI-Service, AI Model, Shared With (Prompt Type removed) */}
          <Box
            sx={{
              display: "flex",
              gap: "32px",
            }}
          >
            {/* AI-Service Dropdown (new) */}
            <Box sx={{ flex: 1 }}>
              <TypographyInline
                size={"sm"}
                color={GREY.QUATINARY}
                fontWeight={fontWeight.MEDIUM}
                label="AI-Service"
                style={{ marginBottom: "8px", display: "block" }}
              />
              <AutoCompleteCustom
                items={SERVICE_OPTIONS}
                multiple={false}
                valueState={serviceValueState}
                setValueState={currentMode !== "view" ? setServiceValueState : () => { }}
                idState={serviceIdState}
                setIdState={currentMode !== "view" ? setServiceIdState : () => { }}
                onChange={handleServiceACChange}
                isSearchEnable={false}
                handleClick={() => { }}
                size={"sm36"}
                dropdownId={"id"}
                variant={currentMode === "view" ? "disabled" : "default"}
                papermenuclass={classes.paperMenu1}
              />
            </Box>

            {/* AI Model (AutoCompleteCustom - service filtered) */}
            <Box sx={{ flex: 1 }}>
              <TypographyInline
                size={"sm"}
                color={GREY.QUATINARY}
                fontWeight={fontWeight.MEDIUM}
                label="AI Model"
                style={{ marginBottom: "8px", display: "block" }}
              />
              <AutoCompleteCustom
                items={aiModelSearch ? filteredAiModelList : modelItems}                   // built from MODEL_OPTIONS[selectedService]
                multiple={false}
                valueState={modelValueState}
                setValueState={currentMode !== "view" ? setModelValueState : () => { }}
                idState={modelIdState}
                setIdState={currentMode !== "view" ? setModelIdState : () => { }}
                onChange={handleModelACChange}
                isSearchEnable={true}
                handleSearch={handleAiModelSearch}
                handleClick={() => { }}               // keeps behavior consistent with other uses
                size={"sm36"}
                dropdownId={"id"}
                variant={currentMode === "view" || !selectedService ? "disabled" : "default"}
                papermenuclass={classes.paperMenu1}
              />
            </Box>

            {/* Shared With (moved into Prompt Type position) */}
            <Box sx={{ flex: 1 }}>
              <TypographyInline
                size={"sm"}
                color={GREY.QUATINARY}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.SHARED_WITH}
                style={{ marginBottom: "8px", display: "block" }}
              />
              <RadioButton
                value={viewType}
                onChange={handleChange}
                items={radioButtonData}
                disabled={currentMode === "view"}
              />
            </Box>
          </Box>
        </Box>

        {/* Main Prompt & Preview Section */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: "20px",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "16px",
            backgroundColor: "#fafafa",
            minHeight: 0,
          }}
        >
          {/* Left Section - Prompt Input (50% width) */}
          <Box
            sx={{
              flex: "1",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <TypographyInline
              size={"sm"}
              color={GREY.QUATINARY}
              fontWeight={fontWeight.SEMIBOLD}
              label="Write Your Prompt"
            />

            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <TextArea
                placeholder="Enter your prompt here..."
                isResize={currentMode !== "view"}
                value={promptBody}
                onChange={(e) => setPromptBody(e.target.value)}
                disabled={currentMode === "view"}
                error={validations.promptBody}
                style={{
                  height: "100%",
                  minHeight: "200px",
                  resize: "none",
                  border: validations.promptBody
                    ? "1px solid #ff4444"
                    : "1px solid #d0d0d0",
                  borderRadius: "6px",
                }}
              />
              {validations.promptBody && (
                <Typography
                  size={"xs"}
                  color="#ff4444"
                  fontWeight={fontWeight.REGULAR}
                  label={validations.promptBody}
                  style={{ marginTop: "4px" }}
                />
              )}
            </Box>

            {/* Generate Button */}
            {currentMode !== "view" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  paddingTop: "8px",
                }}
              >
                <Button
                  label={STRINGS.GENERATE_RESPONSE}
                  size={"lg44"}
                  variant={"primary"}
                  isLoading={isGenerating}
                  onClick={generateResult}
                  type="button"
                />
              </Box>
            )}
          </Box>

          {/* Right Section - Preview (50% width) */}
          <Box
            sx={{
              flex: "1",
              display: "flex",
              flexDirection: "column",
              borderLeft: "1px solid #e0e0e0",
              paddingLeft: "20px",
            }}
          >
            <TypographyInline
              size={"sm"}
              color={GREY.QUATINARY}
              fontWeight={fontWeight.SEMIBOLD}
              label={STRINGS.PREVIEW}
              style={{ marginBottom: "12px" }}
            />

            <Box
              sx={{
                flex: 1,
                border: "1px solid #d0d0d0",
                borderRadius: "6px",
                backgroundColor: "#ffffff",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {!generatedEmail ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  <Typography
                    size={"xl"}
                    color={BRAND.SECONDARY}
                    fontWeight={fontWeight.BOLD}
                    label={STRINGS.AI_ASSISTANT}
                    style={{ marginBottom: "8px" }}
                  />
                  <Typography
                    size={"sm"}
                    color={GREY.SECONDARY}
                    fontWeight={fontWeight.REGULAR}
                    label={STRINGS.GET_THE_BEST}
                  />
                </Box>
              ) : (
                <CustomTextArea
                  height="100%"
                  value={generatedEmail}
                  onChange={(e) => handleGeneratedEmailChange(e.target.value)}
                  isResize={currentMode !== "view"}
                  disabled={currentMode === "view"}
                  style={{
                    height: "100%",
                    border: "none",
                    borderRadius: "6px",
                    resize: "none",
                    flex: 1,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer buttons */}
      <Box className={classes.footerCreateTemplate}>
        {currentMode === "view" ? (
          <div className={classes.padRight}>
            <Button
              label="Close"
              variant={"ghost"}
              size={"sm36"}
              onClick={handleClose}
              fontSize={fontSize.SM}
            />
          </div>
        ) : (
          <>
            <div>
              <Button
                label={STRINGS.PROMPT_CANCEL_BTN}
                variant={"ghost"}
                size={"sm36"}
                onClick={handleCancel}
                fontSize={fontSize.SM}
              />
            </div>
            <div className={classes.padRight}>
              <Button
                label={
                  isEditing
                    ? STRINGS.PROMPT_UPDATE_BTN
                    : STRINGS.PROMPT_CREATE_BTN
                }
                size={"sm36"}
                variant={"primary"}
                onClick={submitData}
                disabled={loading || !generatedEmail}
                isLoading={loading}
                fontSize={fontSize.SM}
              />
            </div>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CreatePrompt;
